// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Bundlers like Metro may inject a `process.env` object into React Native bundles.
// Declare the minimal shape on globalThis so we can access it type-safely without
// pulling in @types/node.
// Using `globalThis.process` (property access) instead of bare `process` avoids
// ReferenceError when the binding doesn't exist at all.
declare global {
  // eslint-disable-next-line no-var
  var process:
    | { env?: Record<string, string | undefined>; emitWarning?: (warning: string) => void }
    | undefined;
}

/**
 * Returns the value of the specified environment variable.
 * In React Native environments, this checks for a globally-defined `process.env`
 * which may be injected by bundlers like Metro.
 *
 * @internal
 */
export function getEnvironmentVariable(name: string): string | undefined {
  return globalThis.process?.env?.[name];
}

/**
 * Emits a warning via `process.emitWarning` if available.
 *
 * @internal
 */
export function emitNodeWarning(warning: string): void {
  globalThis.process?.emitWarning?.(warning);
}

/**
 * A constant that indicates whether the environment the code is running is a Web Browser.
 */
export const isBrowser: boolean = false;

/**
 * A constant that indicates whether the environment the code is running is a Web Worker.
 */
export const isWebWorker: boolean = false;

/**
 * A constant that indicates whether the environment the code is running is Deno.
 */
export const isDeno: boolean = false;

/**
 * A constant that indicates whether the environment the code is running is Bun.sh.
 */
export const isBun: boolean = false;

/**
 * A constant that indicates whether the environment the code is running is a Node.js compatible environment.
 */
export const isNodeLike: boolean = false;

/**
 * A constant that indicates whether the environment the code is running is Node.JS.
 */
export const isNodeRuntime: boolean = false;

/**
 * A constant that indicates whether the environment the code is running is in React-Native.
 */
// https://github.com/facebook/react-native/blob/main/packages/react-native/Libraries/Core/setUpNavigator.js
export const isReactNative: boolean = true;
